import type {
  MarketingAnalyticsSnapshot,
  MarketingAppProfile,
  MarketingCreativeHypothesis,
  MarketingPostedVideo,
  MarketingPostedVideoMetricSnapshot,
  MarketingRunDecision,
  MarketingSkillRun,
  MarketingSyncRun,
} from './marketingCockpitService.ts';

export type WeeklyMarketingActionStatus = MarketingRunDecision | 'ready_to_test';

export interface WeeklyMarketingAction {
  id: string;
  appId: MarketingAppProfile['id'];
  source: string;
  hypothesis: string;
  action: string;
  creative: string;
  metric: string;
  decisionRule: string;
  status: WeeklyMarketingActionStatus;
  evidence: string[];
}

export interface ViralTrendInput {
  id: string;
  source: string;
  title: string;
  platform: string;
  url?: string;
  views: number;
  hashtags: string[];
  audio?: string;
  whyItMatters: string;
}

export type TractionVerdict = 'too_early' | 'weak' | 'promising' | 'winner';

export interface PostedVideoTraction {
  verdict: TractionVerdict;
  summary: string;
  latest?: MarketingPostedVideoMetricSnapshot;
  previous?: MarketingPostedVideoMetricSnapshot;
  engagementRate: number;
  viewGrowth: number;
}

const ACTIONABLE_DECISIONS = new Set<MarketingRunDecision>(['accepted', 'pending', 'needs_revision']);
const GENERIC_HASHTAGS = new Set([
  '#capcut',
  '#fyp',
  '#foryou',
  '#foryoupage',
  '#viral',
  '#trending',
  '#tiktok',
  '#reels',
  '#shorts',
]);
const GENERIC_AUDIO = new Set(['capcut', 'original sound', 'original audio']);

const APP_RELEVANCE: Record<MarketingAppProfile['id'], { include: string[]; exclude: string[] }> = {
  spontane: {
    include: [
      'tel aviv',
      'telaviv',
      'jaffa',
      'yafo',
      'nightlife',
      'event',
      'events',
      'tonight',
      'weekend',
      'date night',
      'things to do',
      'hidden place',
      'bar',
      'club',
      'concert',
      'restaurant',
      'beach',
      'תל אביב',
      'יפו',
      'אירוע',
      'אירועים',
      'הופעה',
      'הופעות',
      'מסיבה',
      'בר',
      'ברים',
      'דייט',
      'בילוי',
      'לבלות',
      'הלילה',
      'סופש',
      'סופ"ש',
      'חוף',
      'מסעד',
    ],
    exclude: [
      'iran',
      'missile',
      'missiles',
      'army',
      'military',
      'war',
      'attack',
      'strikes',
      'breaking',
      'fox news',
      'al jazeera',
      'israeli army',
      'איראן',
      'טיל',
      'טילים',
      'מלחמה',
      'צבא',
      'צהל',
      'צה"ל',
      'פיקוד העורף',
      'אזעק',
      'מקלט',
      'מתקפה',
    ],
  },
  emulsify: {
    include: [
      'recipe',
      'recipes',
      'cooking',
      'cook',
      'meal prep',
      'kitchen',
      'food',
      'chef',
      'baking',
      'ai cooking',
      'recipe app',
      'יומן בישול',
      'מתכון',
      'מתכונים',
      'בישול',
      'אוכל',
      'מטבח',
    ],
    exclude: ['war', 'politics', 'missile', 'breaking', 'attack', 'מלחמה', 'טילים', 'איראן'],
  },
};

function asRecord(value: unknown): Record<string, any> {
  return value && typeof value === 'object' ? (value as Record<string, any>) : {};
}

function compactText(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function truncateText(value: string, maxLength: number): string {
  const compacted = compactText(value);
  return compacted.length > maxLength ? `${compacted.slice(0, maxLength - 1).trim()}...` : compacted;
}

function numberFromUnknown(value: unknown): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return Number(value.replace(/[^\d.]/g, '')) || 0;
  return 0;
}

function stringsFromUnknown(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        const rec = asRecord(item);
        return rec.name || rec.title || rec.hashtag || item;
      })
      .map(String)
      .map((item) => item.trim())
      .filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(/[,\s]+/)
      .map((item) => item.trim())
      .filter((item) => item.startsWith('#') || item.length > 1);
  }
  return [];
}

function hashtagsFromText(text: string): string[] {
  return Array.from(new Set(text.match(/#[\p{L}\p{N}_]+/gu) || []));
}

function audioFromRecord(rec: Record<string, any>): string | undefined {
  const music = asRecord(rec.music || rec.sound || rec.audio || rec.track);
  const raw = rec.audio_title || rec.sound_title || rec.music_title || music.title || music.name || music.track_name;
  if (!raw) return undefined;
  const audio = compactText(String(raw));
  return GENERIC_AUDIO.has(audio.toLowerCase()) ? undefined : audio;
}

function firstUsefulRecommendationLine(run: MarketingSkillRun): string {
  const lines = run.recommendation
    .split('\n')
    .map(compactText)
    .filter(Boolean);
  const hookLine = lines.find((line) => /^hook\s*\d\s*:/i.test(line));
  if (hookLine) return truncateText(hookLine.replace(/^hook\s*\d\s*:\s*/i, ''), 140);

  const firstLine = lines.find((line) => {
    const normalized = line.toLowerCase();
    return !normalized.startsWith('evidence-based')
      && !normalized.startsWith('evidence:')
      && !normalized.startsWith('top trend')
      && !normalized.startsWith('top evidence')
      && !normalized.startsWith('virlo orbit')
      && !normalized.startsWith('dataforseo');
  });

  return truncateText(firstLine || compactText(run.objective) || `Run a ${run.skillLabel} test.`, 140);
}

function analyticsEvidence(snapshot: MarketingAnalyticsSnapshot | null): string | null {
  if (!snapshot) return null;
  if (snapshot.status !== 'completed') return snapshot.setupHint || snapshot.summary || null;

  return `${snapshot.totals.installs} installs, ${snapshot.totals.activations} activations, ${snapshot.totals.activationRate}% activation rate in the latest analytics sync.`;
}

function matchingCreative(profile: MarketingAppProfile, metric: string): MarketingCreativeHypothesis | undefined {
  const normalizedMetric = metric.toLowerCase();
  return profile.creativeHypotheses.find((idea) => idea.metric.toLowerCase().includes(normalizedMetric))
    || profile.creativeHypotheses[0];
}

function actionFromSkillRun(
  profile: MarketingAppProfile,
  run: MarketingSkillRun,
  snapshot: MarketingAnalyticsSnapshot | null,
  trend?: ViralTrendInput,
): WeeklyMarketingAction {
  const creative = matchingCreative(profile, run.measurementTarget);
  const metric = run.measurementTarget || creative?.metric || profile.measurementEvents[1]?.label || 'Activation';
  const evidence = [
    trend ? `Viral input: ${trend.audio || trend.hashtags[0] || trend.title}` : null,
    ...run.inputsUsed.slice(0, 2),
    analyticsEvidence(snapshot),
  ].filter(Boolean) as string[];

  return {
    id: `run-${run.id}`,
    appId: profile.id,
    source: run.skillLabel,
    hypothesis: firstUsefulRecommendationLine(run),
    action: trend
      ? `${run.nextActions[0] || `Run one ${profile.name} marketing test from this recommendation.`} Use ${trend.audio ? `audio "${trend.audio}"` : trend.hashtags[0] || 'the top Virlo format'} as the creative constraint.`
      : run.nextActions[0] || `Run one ${profile.name} marketing test from this recommendation.`,
    creative: creative?.hook || profile.corePromise,
    metric,
    decisionRule: `Keep only if ${metric} improves versus the other tests this week; otherwise stop or revise after 7 days.`,
    status: run.decision,
    evidence,
  };
}

function actionFromCreativeIdea(
  profile: MarketingAppProfile,
  idea: MarketingCreativeHypothesis,
  snapshot: MarketingAnalyticsSnapshot | null,
  trend?: ViralTrendInput,
): WeeklyMarketingAction {
  return {
    id: `idea-${idea.id}`,
    appId: profile.id,
    source: idea.channel,
    hypothesis: `"${idea.hook}" will move ${idea.metric} because it tests ${idea.angle.toLowerCase()}.`,
    action: trend
      ? `Publish one ${idea.channel} test using ${trend.audio ? `audio "${trend.audio}"` : trend.hashtags[0] || 'the top Virlo hashtag'} as the trend input.`
      : `Publish or run one small ${idea.channel} test this week.`,
    creative: idea.hook,
    metric: idea.metric,
    decisionRule: `After 48-72 hours, keep only if ${idea.metric} beats the other weekly tests.`,
    status: 'ready_to_test',
    evidence: [
      `Brand promise: ${profile.corePromise}`,
      trend ? `Viral input: ${trend.audio || trend.hashtags[0] || trend.title}` : null,
      analyticsEvidence(snapshot),
    ].filter(Boolean) as string[],
  };
}

export function buildWeeklyMarketingActions(
  profile: MarketingAppProfile,
  runs: MarketingSkillRun[],
  snapshot: MarketingAnalyticsSnapshot | null,
  maxActions = 3,
  trendInputs: ViralTrendInput[] = [],
): WeeklyMarketingAction[] {
  const runActions = runs
    .filter((run) => ACTIONABLE_DECISIONS.has(run.decision))
    .slice(0, maxActions)
    .map((run, index) => actionFromSkillRun(profile, run, snapshot, trendInputs[index % Math.max(trendInputs.length, 1)]));

  if (runActions.length >= maxActions) return runActions;

  const usedCreative = new Set(runActions.map((action) => action.creative));
  const ideaActions = profile.creativeHypotheses
    .filter((idea) => !usedCreative.has(idea.hook))
    .slice(0, maxActions - runActions.length)
    .map((idea, index) => actionFromCreativeIdea(profile, idea, snapshot, trendInputs[index % Math.max(trendInputs.length, 1)]));

  return [...runActions, ...ideaActions];
}

export function summarizeActivation(snapshot: MarketingAnalyticsSnapshot | null): string {
  if (!snapshot) return 'Analytics not connected yet';
  if (snapshot.status !== 'completed') return snapshot.summary || 'Analytics sync needs setup';

  return `${snapshot.totals.activations} activations from ${snapshot.totals.installs} installs`;
}

function usefulHashtags(input: string[]): string[] {
  return Array.from(new Set(input
    .map((tag) => tag.startsWith('#') ? tag : `#${tag}`)
    .filter((tag) => !GENERIC_HASHTAGS.has(tag.toLowerCase()))));
}

function relevanceScore(profile: MarketingAppProfile | undefined, trend: ViralTrendInput): number {
  if (!profile) return trend.audio || trend.hashtags.length ? 1 : 0;
  const config = APP_RELEVANCE[profile.id];
  const haystack = `${trend.title} ${trend.hashtags.join(' ')} ${trend.audio || ''}`.toLowerCase();
  if (config.exclude.some((term) => haystack.includes(term.toLowerCase()))) return -100;

  const profileTerms = [
    ...config.include,
    ...profile.contentAngles,
    ...profile.virlo.trackedNiches,
    ...profile.virlo.trackedKeywords,
    ...profile.dataForSeo.trackedKeywords,
  ];
  const matches = profileTerms.filter((term) => term && haystack.includes(term.toLowerCase())).length;
  return matches + (trend.audio ? 2 : 0) + Math.min(trend.hashtags.length, 3);
}

export function buildViralTrendInputs(
  runs: MarketingSyncRun[],
  profile?: MarketingAppProfile,
  maxInputs = 6,
): ViralTrendInput[] {
  const latest = runs.find((run) => run.status === 'completed' && run.sourceDetails?.length);
  const virlo = latest?.sourceDetails?.find((source) => source.name === 'Virlo MCP/API');
  const sample = asRecord(virlo?.sample);
  const videos = Array.isArray(sample.videos) ? sample.videos : [];
  const trends = Array.isArray(sample.trends) ? sample.trends : [];
  const candidates = [...videos, ...trends];

  return candidates
    .map((item, index): ViralTrendInput => {
      const rec = asRecord(item);
      const title = String(rec.description || rec.title || rec.name || rec.summary || rec.url || `Trend ${index + 1}`);
      const hashtags = usefulHashtags([
        ...stringsFromUnknown(rec.hashtags || rec.hashtag_names || rec.tags),
        ...hashtagsFromText(title),
      ]).slice(0, 5);
      const views = numberFromUnknown(rec.views || rec.view_count || rec.total_views || rec.plays || rec.play_count);
      const platform = String(rec.platform || rec.source || rec.network || 'short-form');

      return {
        id: String(rec.id || rec.video_id || rec.url || `trend-${index}`),
        source: 'Virlo',
        title: truncateText(title, 110),
        platform,
        url: rec.url ? String(rec.url) : undefined,
        views,
        hashtags,
        audio: audioFromRecord(rec),
        whyItMatters: views
          ? `${views.toLocaleString()} views, filtered for app relevance and reusable creative signals.`
          : 'Relevant creative signal from Virlo; validate with your own post metrics.',
      };
    })
    .filter((input) => input.audio || input.hashtags.length)
    .map((input) => ({ input, score: relevanceScore(profile, input) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score || b.input.views - a.input.views)
    .map(({ input }) => input)
    .slice(0, maxInputs);
}

export function assessPostedVideoTraction(video: MarketingPostedVideo): PostedVideoTraction {
  const snapshots = [...video.snapshots].sort((a, b) => a.capturedAt.localeCompare(b.capturedAt));
  const latest = snapshots[snapshots.length - 1];
  const previous = snapshots[snapshots.length - 2];

  if (!latest) {
    return {
      verdict: 'too_early',
      summary: 'Add the first metrics snapshot after posting.',
      engagementRate: 0,
      viewGrowth: 0,
    };
  }

  const engagement = latest.likes + latest.comments + latest.shares + latest.saves;
  const engagementRate = latest.views ? engagement / latest.views : 0;
  const viewGrowth = previous ? latest.views - previous.views : latest.views;

  if (latest.views < 200 && snapshots.length < 2) {
    return {
      verdict: 'too_early',
      summary: `${latest.views.toLocaleString()} views so far. Wait for one more snapshot before judging.`,
      latest,
      previous,
      engagementRate,
      viewGrowth,
    };
  }

  if ((latest.views >= 2500 && engagementRate >= 0.05) || (viewGrowth >= 1500 && engagementRate >= 0.04)) {
    return {
      verdict: 'winner',
      summary: `Winner: ${latest.views.toLocaleString()} views with ${(engagementRate * 100).toFixed(1)}% engagement.`,
      latest,
      previous,
      engagementRate,
      viewGrowth,
    };
  }

  if (latest.views >= 750 || viewGrowth >= 500 || engagementRate >= 0.04) {
    return {
      verdict: 'promising',
      summary: `Promising: ${viewGrowth.toLocaleString()} new views since the previous snapshot.`,
      latest,
      previous,
      engagementRate,
      viewGrowth,
    };
  }

  return {
    verdict: 'weak',
    summary: `Weak so far: ${latest.views.toLocaleString()} views and ${(engagementRate * 100).toFixed(1)}% engagement.`,
    latest,
    previous,
    engagementRate,
    viewGrowth,
  };
}
